import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getNewSolutions, getTrendingSolutions } from '@/hooks/useSolutionFiltering';
import { SolutionCardProps } from '@/components/SolutionCard';

export type TrendingFilter = 'new' | 'trending-week' | 'trending-month';

interface TrendingDataResult {
  solutions: SolutionCardProps[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to manage trending and new solutions data
 */
export const useTrendingData = (selectedFilter: TrendingFilter = 'new'): TrendingDataResult => {
  // Query configuration based on selected filter
  const queryKey = [`trending-solutions-${selectedFilter}`];
  
  const queryFn = async () => {
    switch (selectedFilter) {
      case 'new':
        return await getNewSolutions(3);
      case 'trending-week':
        return await getTrendingSolutions('week', 3);
      case 'trending-month':
        return await getTrendingSolutions('month', 3);
      default:
        return await getNewSolutions(3);
    }
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoized transformation of raw database data to SolutionCardProps format
  const transformedSolutions = useMemo(() => {
    if (data && Array.isArray(data)) {
      return data.map((solution: any) => {
        // Extract category from solution_categories if available
        const category = solution.solution_categories?.[0]?.categories?.name || 'Other';
        
        // Extract tags from solution_tags if available
        const tags = solution.solution_tags
          ?.map((tagRel: any) => tagRel.tags?.name)
          .filter(Boolean) || [];
          
        return {
          id: solution.id,
          name: solution.name,
          category,
          shortDescription: solution.short_description,
          tags,
          featured: solution.featured,
          image: solution.image_url,
          slug: solution.slug
        };
      });
    }
    return [];
  }, [data]);

  return {
    solutions: transformedSolutions,
    isLoading,
    isError,
    error: error as Error | null,
    refetch
  };
};

/**
 * Hook specifically for getting new solutions (convenience wrapper)
 */
export const useNewSolutions = () => {
  return useTrendingData('new');
};

/**
 * Hook specifically for getting trending solutions this week (convenience wrapper)
 */
export const useTrendingWeek = () => {
  return useTrendingData('trending-week');
};

/**
 * Hook specifically for getting trending solutions this month (convenience wrapper)
 */
export const useTrendingMonth = () => {
  return useTrendingData('trending-month');
}; 